import React, { useEffect, useState } from 'react';
import { CheckCircle, Edit3, Save, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { formatDateHelper } from '../../utils/dateHelpers';

// Refactored to a form-only component. Props:
// - post: current post object
// - onScheduleSubmit: (selectedSocialAccounts) => void
// - onEditSubmit: (payload) => void
// - socialAccounts: array of connected accounts
const SchedulePost = ({ post, onScheduleSubmit, onEditSubmit, socialAccounts = [] }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSocialAccounts, setSelectedSocialAccounts] = useState(post?.socialPlatforms || []);

  const { register, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      caption: post?.caption || '',
      scheduledAt: post?.scheduledAt
        ? formatDateHelper(post.scheduledAt, "yyyy-MM-dd'T'HH:mm")
        : '',
    },
  });

  useEffect(() => {
    if (post) {
      reset({
        caption: post.caption || '',
        scheduledAt: post.scheduledAt
          ? formatDateHelper(post.scheduledAt, "yyyy-MM-dd'T'HH:mm")
          : '',
      });
      setSelectedSocialAccounts(post.socialPlatforms || []);
      setIsEditMode(false);
    }
  }, [post, reset]);

  const toggleEditMode = () => setIsEditMode((v) => !v);

  const handleSocialAccountToggle = (platform) => {
    setSelectedSocialAccounts((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const onEditFormSubmit = (values) => {
    let scheduledAt = '';
    if (values.scheduledAt) {
      try {
        scheduledAt = formatDateHelper(values.scheduledAt, 'yyyy-MM-dd HH:mm:ss');
      } catch (e) {
        // surface error handling to parent
        onEditSubmit?.({ error: 'Invalid date format' });
        return;
      }
    }

    const payload = {
      captions: values.caption || '',
      scheduledAt,
    };
    onEditSubmit?.({ postId: post.id, data: payload });
    setIsEditMode(false);
  };

  const canSchedule = selectedSocialAccounts.length > 0;

  return (
    <div className='h-full overflow-y-auto space-y-2 lg:space-y-4 '>
      <div className='space-y-0'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-semibold text-gray-900'>Post Details</h3>
          <button
            onClick={toggleEditMode}
            className='flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors'
          >
            <Edit3 size={16} />
            {isEditMode ? 'Cancel' : 'Edit'}
          </button>
        </div>

        {isEditMode ? (
          <form onSubmit={handleSubmit(onEditFormSubmit)} className='space-y-4'>
            <div className='flex-1 flex flex-col min-h-0'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>Caption</label>
              <textarea
                {...register('caption')}
                className='w-full text-sm lg:text-lg flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none h-auto lg:min-h-[150px]'
                placeholder='Enter caption...'
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Scheduled Date & Time
              </label>
              <input
                type='datetime-local'
                {...register('scheduledAt')}
                className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              />
            </div>

            <button
              type='submit'
              className='w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
            >
              <Save size={18} />
              Save Changes
            </button>
          </form>
        ) : (
          <div className='space-y-3'>
            <div>
              <p className='text-sm font-medium text-gray-500'>Caption</p>
              <p className='mt-1 text-gray-900 text-sm lg:text-lg break-words'>
                {post?.caption || 'No caption'}
              </p>
            </div>

            {post?.scheduledAt && (
              <div>
                <p className='text-sm font-medium text-gray-500'>Scheduled For</p>
                <p className='mt-1 text-gray-900'>{formatDateHelper(post.scheduledAt, 'PPpp')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {!isEditMode && (
        <>
          <div className='space-y-2 lg:space-y-4'>
            <h3 className='text-lg font-semibold text-gray-900'>Select Social Accounts</h3>

            {socialAccounts.length === 0 ? (
              <div className='flex items-center gap-2'>
                <AlertCircle className='text-amber-600' size={20} />
                <p className='text-sm text-amber-800'>
                  No social accounts connected.{' '}
                  <Link to='/socials' className='font-medium underline hover:text-amber-900'>
                    Connect accounts
                  </Link>
                </p>
              </div>
            ) : (
              <div className='space-y-2'>
                {socialAccounts.map((account) => (
                  <label
                    key={account.platform}
                    className='flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors'
                  >
                    <input
                      type='checkbox'
                      checked={selectedSocialAccounts.includes(account.platform)}
                      onChange={() => handleSocialAccountToggle(account.platform)}
                      className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                    />
                    <div className='flex items-center gap-2'>
                      <span className='font-medium text-gray-900'>{account.platform}</span>
                      <span className='text-sm text-gray-500'>@{account.username}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => onScheduleSubmit?.(selectedSocialAccounts)}
            disabled={!canSchedule}
            className='w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            <CheckCircle size={20} />
            Schedule Post
          </button>
        </>
      )}
    </div>
  );
};

export default SchedulePost;
